<script setup>

import {computed, onMounted, reactive, ref, watch} from "vue";
import {useAdminStore} from "@/store/admin";
import {useToast} from "vue-toast-notification";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import FormInput from "@/components/ui/FormInput.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import TablePager from "@/components/shared/TablePager.vue";
import AdminModal from "@/components/AdminView/AdminModal.vue";
import FileButton from "@/components/ui/FileButton.vue";
import {openImagePreview} from "@/composables/image-preview";

const adminStore = useAdminStore();
const toast = useToast();

const items = computed(() => adminStore.news);

const NEWS_PER_PAGE_STORAGE_KEY = 'adminNewsPerPage';
const DATE_FILTER_STORAGE_KEY = 'adminNewsDateFilter';
const currentPage = ref(0);
const PER_PAGE = ref(parseInt(localStorage.getItem(NEWS_PER_PAGE_STORAGE_KEY)) || 50);

const countPages = computed(() => Math.max(1, Math.ceil((adminStore.totalNews || 0) / PER_PAGE.value)));
const hasMoreData = computed(() => (adminStore.totalNews || 0) > (currentPage.value + 1) * PER_PAGE.value);

const dateFilter = ref(localStorage.getItem(DATE_FILTER_STORAGE_KEY) || "all");
watch(dateFilter, value => localStorage.setItem(DATE_FILTER_STORAGE_KEY, value));

function toDateParam(d) {
    const pad = n => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function dateRange() {
    const now = new Date();
    const from = new Date(now);
    switch (dateFilter.value) {
        case "today": break;
        case "week": from.setDate(now.getDate() - 6); break;
        case "month": from.setMonth(now.getMonth() - 1); break;
        case "year": from.setFullYear(now.getFullYear() - 1); break;
        default: return {};
    }
    return {date_from: toDateParam(from), date_to: toDateParam(now)};
}

async function loadNews() {
    await adminStore.loadNewsData({page: currentPage.value + 1, per_page: PER_PAGE.value, ...dateRange()});
}

function setDateFilter() {
    currentPage.value = 0;
    loadNews();
}

async function setPage(p) {
    if (p < 0 || p >= countPages.value) return;
    currentPage.value = p;
    await loadNews();
}

function setPerPage(n) {
    const val = parseInt(n);
    if (!val || val < 1) return;
    PER_PAGE.value = val;
    localStorage.setItem(NEWS_PER_PAGE_STORAGE_KEY, String(val));
    currentPage.value = 0;
    loadNews();
}

const pagination = {
    loading: computed(() => adminStore.loading),
    currentPage,
    countPages,
    hasMoreData,
    perPage: PER_PAGE,
    setPage,
    setPerPage,
    firstPage: () => setPage(0),
    decPage: () => setPage(currentPage.value - 1),
    incPage: () => setPage(currentPage.value + 1),
    lastPage: () => setPage(countPages.value - 1),
};

const editing = ref(null);
const showForm = ref(false);
const form = reactive(defaultForm());
const selectedImage = ref(null);
const imagePreview = ref("");

function selectImage(file) {
    selectedImage.value = file || null;
    if (imagePreview.value) URL.revokeObjectURL(imagePreview.value);
    imagePreview.value = selectedImage.value ? URL.createObjectURL(selectedImage.value) : "";
}

function defaultForm() {
    return {title: "", body: "", author: "", like_count: 0, view_count: 0};
}

function resetForm() {
    Object.assign(form, defaultForm());
    selectedImage.value = null;
    if (imagePreview.value) URL.revokeObjectURL(imagePreview.value);
    imagePreview.value = "";
}

function formatDate(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("ru-RU") + " " + d.toLocaleTimeString("ru-RU", {hour: "2-digit", minute: "2-digit"});
}

function openCreate() {
    editing.value = {};
    showForm.value = true;
    resetForm();
}

function openEdit(item) {
    editing.value = item;
    showForm.value = true;
    Object.assign(form, {
        title: item.title || "",
        body: item.body || "",
        author: item.author || "",
        like_count: item.like_count ?? 0,
        view_count: item.view_count ?? 0,
    });
}

function cancelForm() {
    editing.value = null;
    showForm.value = false;
    resetForm();
}

async function saveHandler() {
    const payload = {
        title: form.title,
        body: form.body,
        author: form.author,
        like_count: Number(form.like_count) || 0,
        view_count: Number(form.view_count) || 0,
    };
    const isEdit = !!editing.value?.id;
    const ok = isEdit
        ? await adminStore.updateItem('news', editing.value.id, payload)
        : await adminStore.createItem('news', payload);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        const savedItem = isEdit ? editing.value : ok;
        if (selectedImage.value && savedItem?.id) {
            await adminStore.uploadNewsImage(savedItem.id, selectedImage.value);
            if (adminStore.error) {
                toast.error(adminStore.error);
                await loadNews();
                return;
            }
        }
        toast.success(isEdit ? "Новость обновлена" : "Новость создана");
        cancelForm();
        await loadNews();
    }
}

async function deleteHandler(item) {
    if (!confirm(`Удалить новость "${item.title || item.id}"?`)) return;
    const ok = await adminStore.deleteItem('news', item.id);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        toast.success("Новость удалена");
        await loadNews();
    }
}

onMounted(() => {
    loadNews();
});

</script>

<template>
  <div class="adminCrud">
    <MainPreloader
        v-if="adminStore.loading"
        position="fixed"
        :size="80"
        :stroke="3"
    />

    <div class="adminCrud__toolbar">
      <div class="adminCrud__toolbarTitle">Новости</div>
      <div class="adminCrud__toolbarActions">
        <select
            v-model="dateFilter"
            class="adminCrud__select adminCrud__select--filter"
            aria-label="Фильтр по дате"
            @change="setDateFilter"
        >
          <option value="all">Все</option>
          <option value="today">Сегодня</option>
          <option value="week">За неделю</option>
          <option value="month">За месяц</option>
          <option value="year">За год</option>
        </select>
        <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="openCreate">
          <template #caption>Добавить новость</template>
        </ActionButton>
      </div>
    </div>

    <AdminModal :visible="showForm" :title="editing?.id ? 'Редактирование новости' : 'Новая новость'" @close="cancelForm">
      <div class="adminCrud__formGrid">
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Заголовок</label>
          <form-input v-model="form.title"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Автор</label>
          <form-input v-model="form.author"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Лайков</label>
          <form-input v-model="form.like_count"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Просмотров</label>
          <form-input v-model="form.view_count"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide adminCrud__field--content">
          <label class="adminCrud__label">Текст</label>
          <form-input v-model="form.body" rows="20"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Изображение (JPEG, PNG или GIF)</label>
          <FileButton accept="image/jpeg,image/png,image/gif" @select="selectImage"/>
          <img
              v-if="editing?.img_url && !selectedImage"
              :src="editing.img_url"
              alt="Текущее изображение"
              class="adminCrud__imagePreview"
              @click="openImagePreview(editing.img_url)"
          />
          <img
              v-if="imagePreview"
              :src="imagePreview"
              alt="Предпросмотр изображения"
              class="adminCrud__imagePreview"
              @click="openImagePreview(imagePreview)"
          />
        </div>
      </div>
      <template #footer>
        <ActionButton @buttonClick="cancelForm">
          <template #caption>Отмена</template>
        </ActionButton>
        <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="saveHandler">
          <template #caption>Сохранить</template>
        </ActionButton>
      </template>
    </AdminModal>

    <EmptyCard v-if="!items.length && !adminStore.loading"/>

    <TablePager
        v-if="items.length && pagination.countPages.value >= 1"
        :pagination="pagination"
    />

    <table v-if="items.length" class="adminTable">
      <thead>
      <tr>
        <th></th>
        <th>Заголовок</th>
        <th>Действия</th>
        <th>Автор</th>
        <th>Создана</th>
        <th>Лайков</th>
        <th>Просмотров</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="item in items" :key="item.id">
        <td>
          <img
              v-if="item.img_url"
              :src="item.img_url"
              alt=""
              class="adminCrud__thumb"
              @click="openImagePreview(item.img_url)"
          />
          <span v-else class="adminCrud__thumb adminCrud__thumb--empty"></span>
        </td>
        <td>{{ item.title || '—' }}</td>
        <td class="adminCrud__actions">
          <button class="adminCrud__actionButton" title="Изменить" @click="openEdit(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="adminCrud__actionButton adminCrud__actionButton--danger" title="Удалить" @click="deleteHandler(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </td>
        <td>{{ item.author || '—' }}</td>
        <td>{{ formatDate(item.created_at) }}</td>
        <td>{{ item.like_count ?? '—' }}</td>
        <td>{{ item.view_count ?? '—' }}</td>
      </tr>
      </tbody>
    </table>

    <TablePager
        v-if="items.length && pagination.countPages.value >= 1"
        :pagination="pagination"
    />
  </div>
</template>

<style scoped lang="scss">
.adminCrud {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__toolbarTitle {
    font-size: 20px;
    font-weight: 600;
    color: var(--cd-text);
  }

  &__toolbarActions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__select {
    width: 100%;
    height: 48px;
    border-radius: 20px;
    padding: 5px 20px;
    background: var(--cd-field-bg);
    color: var(--cd-field-color);
    border: 1px solid var(--cd-field-border);
    font-size: 14px;
    font-family: var(--cd-default-font);

    &:focus {
      outline: none;
      border-color: var(--cd-base-green);
      box-shadow: 0 0 5px rgba(98, 192, 100, 0.3);
    }

    &--filter {
      width: auto;
      min-width: 145px;
    }
  }

  &__form {
    background: var(--cd-card-bg);
    border-radius: 20px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-align: left;
  }

  &__formTitle {
    color: var(--cd-base-green);
    font-size: 16px;
    font-family: var(--cd-default-font);
  }

  &__formGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @include respond-to('md') {
      grid-template-columns: 1fr;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--wide {
      grid-column: 1 / -1;
    }

    &--content {
      :deep(.formInput) {
        min-height: 30vh;
        height: auto;
        resize: vertical;
        width: 100%;
      }
    }
  }

  &__label {
    color: var(--cd-gray-alt);
    font-size: 13px;
  }

  &__imagePreview {
    max-width: 240px;
    max-height: 180px;
    object-fit: contain;
    cursor: zoom-in;
  }

  &__thumb {
    display: block;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    background: var(--cd-bg-dark);
    cursor: zoom-in;

    &--empty {
      background: transparent;
      border: 1px solid var(--cd-border);
      cursor: default;
    }
  }

  &__formActions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }

  &__actions {
    vertical-align: middle;
    white-space: nowrap;
    font-size: 0;

    .adminCrud__actionButton + .adminCrud__actionButton {
      margin-left: 8px;
    }
  }

  &__actionButton {
    background: transparent;
    border: 1px solid var(--cd-base-green);
    color: var(--cd-base-green);
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &:hover {
      background: var(--cd-base-green);
      color: #ffffff;
    }

    &--danger {
      border-color: var(--cd-danger);
      color: var(--cd-danger);

      &:hover {
        background: var(--cd-danger);
        color: #ffffff;
      }
    }
  }
}

.adminTable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
  text-align: left;

  @include respond-to('md') {
    display: block;
    overflow-x: auto;
  }

  th {
    color: var(--cd-text-muted);
    font-size: 12px;
    font-weight: 400;
    padding: 0 16px 4px;
  }

  td {
    background: var(--cd-card-bg);
    padding: 12px 16px;
    font-size: 14px;
    color: var(--cd-text);

    &:first-child {
      border-radius: 12px 0 0 12px;
    }

    &:last-child {
      border-radius: 0 12px 12px 0;
    }
  }
}
</style>
